import React from 'react';
import { styled } from '@compiled/react';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { CardId } from '../../../../model/types.tsx';
import { DividerStatistics } from '../../divider/divider-statistics/index.tsx';

type Props = {
	edge: Edge;
	droppingOverCardId: CardId;
	droppingOverCardListId: string;
	dropStatisticsCardListId?: string;
};

export function DropIndicator({
	edge,
	droppingOverCardId,
	droppingOverCardListId,
	dropStatisticsCardListId,
}: Props) {
	return isVisualRefreshEnabled() ? (
		<Flex xcss={[dropLineStyles, edge === 'top' && topStyles, edge === 'bottom' && bottomStyles]}>
			{dropStatisticsCardListId && (
				<Flex xcss={informationPillStyles} alignItems="center" justifyContent="center">
					<DividerStatistics
						dividerCardListId={dropStatisticsCardListId}
						isDragIndicator
						edge={edge === 'top' ? 'top' : 'bottom'}
						droppingOverCardListId={droppingOverCardListId}
						droppingOverCardId={droppingOverCardId}
					/>
				</Flex>
			)}
		</Flex>
	) : (
		<DropLine edge={edge}>
			{dropStatisticsCardListId && (
				<InformationPill>
					<DividerStatistics
						dividerCardListId={dropStatisticsCardListId}
						isDragIndicator
						edge={edge === 'top' ? 'top' : 'bottom'}
						droppingOverCardListId={droppingOverCardListId}
						droppingOverCardId={droppingOverCardId}
					/>
				</InformationPill>
			)}
		</DropLine>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropLine = styled.div<{ edge: Edge }>({
	position: 'absolute',
	// Design tokens are in use here, but the linter doesn't recognize it
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	top: (props) => (props.edge === 'top' ? token('space.negative.025') : undefined),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	bottom: (props) => (props.edge === 'bottom' ? token('space.0') : undefined),
	left: 0,
	width: '100%',
	height: '2px',
	zIndex: 1,
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.icon.brand', colors.B300),
	pointerEvents: 'none',
	'&::before': {
		content: "''",
		position: 'absolute',
		height: '4px',
		width: '4px',
		top: '-3px',
		left: '-7px',
		borderRadius: '50%',
		borderWidth: '2px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderColor: token('color.border.brand', colors.B300),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InformationPill = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.icon.brand', colors.B300),
	borderRadius: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingTop: token('space.050'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.100'),
});

const dropLineStyles = xcss({
	position: 'absolute',
	left: token('space.050'),
	width: `calc(100% - ${token('space.050')})`,
	height: '2px',
	zIndex: 'card',
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	backgroundColor: 'color.background.selected.bold',
	pointerEvents: 'none',
	'::before': {
		content: "''",
		position: 'absolute',
		height: '4px',
		width: '4px',
		top: '-3px',
		left: '-7px',
		borderRadius: '50%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		border: `2px solid ${token('color.border.brand', colors.B300)}`,
	},
});

const topStyles = xcss({
	top: token('space.negative.025'),
});

const bottomStyles = xcss({
	bottom: token('space.0'),
});

const informationPillStyles = xcss({
	backgroundColor: 'color.background.selected.bold',
	borderRadius: '4px',
	paddingBlock: 'space.075',
	paddingInline: 'space.150',
	position: 'absolute',
	marginLeft: 'space.100',
});
